import type { CareDefinitions, CareQuestionDefinition, CareTrendPoint } from '@/types/care'

/**
 * Sample data for the care timeline, shown in place of a patient's real history when opted in.
 *
 * Two things it stands in for, both of which are real gaps rather than missing wiring:
 *
 *  1. **No definition endpoint.** The trend read stores hashes, not names — resolving a calculator
 *     to "Anxiety, purple, out of 18" needs the questionnaire definition, which nothing serves to
 *     PA yet. See `services/care-definitions`.
 *  2. **No scores.** Submission ingest leaves every `score` null until CD-94 gives the definition
 *     per-option scores to resolve them from, so a real trend read currently plots nothing.
 *
 * It exists so the chart and table can be reviewed — locally and on staging — before either gap
 * closes. Delete it once both have.
 */

/**
 * Whether sample data may stand in for a patient's missing care history.
 *
 * An explicit opt-in per environment rather than `import.meta.env.DEV`: set `VITE_CARE_FIXTURES=true`
 * on staging to demo the panel there, and leave it unset in production. Vite inlines the value at
 * build time, so where it is unset the comparison folds to `false`, every call into this module is
 * dead, and the module drops out of the bundle entirely.
 */
export const CARE_FIXTURES_ENABLED = import.meta.env.VITE_CARE_FIXTURES === 'true'

// Everything below is either a plain literal or a function. Nothing runs at module load: top-level
// work (a loop filling an object, say) is a side effect the bundler cannot prove safe to drop, which
// is how this data used to reach production bundles even though nothing there could render it.

const SECTIONS = [
  { hash: 'calc_anxiety', name: 'Anxiety', color: 'purple', max: 18, questions: 6 },
  { hash: 'calc_depressed_mood', name: 'Depressed mood', color: 'teal', max: 15, questions: 5 },
  { hash: 'calc_somatic', name: 'Somatic', color: 'green', max: 21, questions: 7 },
  { hash: 'calc_vasomotor', name: 'Vasomotor', color: 'amber', max: 18, questions: 6 },
  { hash: 'calc_sexual_dysfunction', name: 'Sexual dysfunction', color: 'rose', max: 3, questions: 1 }
] as const

const SYMPTOMS = [
  'Difficulty sleeping',
  'Feeling tense or nervous',
  'Hot flushes',
  'Muscle and joint pains',
  'Loss of interest in most things',
  'Feeling unhappy or depressed',
  'Attacks of panic'
]

const OPTIONS = [
  { label: 'Not at all', score: 0 },
  { label: 'A little', score: 1 },
  { label: 'Quite a bit', score: 2 },
  { label: 'Extremely', score: 3 }
]

const fixtureQuestions = (): Record<string, CareQuestionDefinition> => {
  const questions: Record<string, CareQuestionDefinition> = {}

  for (const section of SECTIONS) {
    for (let index = 0; index < section.questions; index++) {
      const hash = `${section.hash}_q${index}`
      questions[hash] = {
        hash,
        title: SYMPTOMS[index % SYMPTOMS.length],
        calculatorHash: section.hash,
        options: Object.fromEntries(
          OPTIONS.map((option, optionIndex) => [
            `${hash}_o${optionIndex}`,
            { label: option.label, score: option.score }
          ])
        )
      }
    }
  }

  return questions
}

/** The definition the sample check-ins were "answered" against — only ever paired with them. */
export const fixtureDefinitions = (): CareDefinitions => ({
  calculators: SECTIONS.map(section => ({
    hash: section.hash,
    name: section.name,
    color: section.color,
    max: section.max
  })),
  total: { name: 'Menopause score', color: 'cyan', max: 75 },
  questions: fixtureQuestions()
})

/** A deterministic 0–1 wobble, so the fixture looks like data rather than a straight line. */
const wobble = (seed: number) => (((Math.sin(seed * 12.9898) * 43758.5453) % 1) + 1) % 1

/**
 * Eight check-ins over eighteen months, improving overall, with one run change partway through —
 * enough to exercise the dotted divider, the percentage axis and the existing/worse pills.
 */
export const buildFixtureTrend = (patientHash: string): CareTrendPoint[] => {
  const questions = fixtureQuestions()
  const start = new Date('2025-02-10T09:00:00.000Z')

  return Array.from({ length: 8 }, (_, index) => {
    const submittedAt = new Date(start.getTime() + index * 70 * 24 * 60 * 60 * 1000)
    // Improvement over time, with per-section noise.
    const decay = 1 - index / 9

    const answers = Object.values(questions).map((question, questionIndex) => {
      const raw = wobble(index * 31 + questionIndex) * 3 * decay
      const score = Math.max(0, Math.min(3, Math.round(raw)))
      const optionIndex = OPTIONS.findIndex(option => option.score === score)

      return {
        questionHash: question.hash,
        value: OPTIONS[optionIndex]?.label ?? null,
        optionHash: `${question.hash}_o${Math.max(optionIndex, 0)}`,
        score,
        flags: [],
        answeredAt: submittedAt.toISOString()
      }
    })

    const values = Object.fromEntries(
      SECTIONS.map(section => [
        section.hash,
        answers
          .filter(answer => questions[answer.questionHash].calculatorHash === section.hash)
          .reduce((sum, answer) => sum + answer.score, 0)
      ])
    )

    return {
      id: `fixture_${index}`,
      type: index === 4 ? 'review' : 'care',
      patientHash,
      userHash: 'fixture_user',
      vendorHash: 'fixture_vendor',
      questionnaireHash: 'fixture_module',
      // The run changes halfway, which the adapter reads as a medication change.
      subscriptionHash: index < 3 ? 'sub_a' : 'sub_b',
      requestHash: `qreq_fixture_${index}`,
      dueAt: submittedAt.toISOString(),
      submittedAt: submittedAt.toISOString(),
      scores: {
        total: Object.values(values).reduce((sum, value) => sum + value, 0),
        values
      },
      flags: [],
      flagged: false,
      answers,
      createdAt: submittedAt.toISOString(),
      updatedAt: submittedAt.toISOString()
    } satisfies CareTrendPoint
  })
}
